import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getProductBySlug } from "../../api/productsApi";
import "./CourseDetail.css";

/**
 * Removes potentially executable/unsafe markup while preserving
 * the HTML formatting used by the course content.
 *
 * This allows:
 *   <h1>, <h2>, <p>, <ul>, <li>, <strong>, <table>, etc.
 *
 * while removing:
 *   <script>, <iframe>, inline event handlers, javascript: URLs, etc.
 */
function sanitizeCourseHtml(html = "") {
  if (!html || typeof html !== "string") {
    return "";
  }

  const parser = new DOMParser();
  const documentNode = parser.parseFromString(html, "text/html");

  // Remove elements that should never come from course content.
  documentNode
    .querySelectorAll(
      "script, iframe, object, embed, form, input, button, textarea, select"
    )
    .forEach((element) => element.remove());

  // Remove executable attributes and unsafe URLs.
  documentNode.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      const attributeName = attribute.name.toLowerCase();
      const attributeValue = attribute.value.trim().toLowerCase();

      // Remove onclick, onerror, onload, etc.
      if (attributeName.startsWith("on")) {
        element.removeAttribute(attribute.name);
        return;
      }

      // Prevent javascript: links.
      if (
        (attributeName === "href" || attributeName === "src") &&
        attributeValue.startsWith("javascript:")
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return documentNode.body.innerHTML;
}

function CourseDetail() {
  const { courseSlug } = useParams();

  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadCourse() {
      try {
        setIsLoading(true);
        setError("");

        const response = await getProductBySlug(courseSlug);

        if (!isMounted) {
          return;
        }

        const courseData =
          response?.data?.product ||
          response?.data?.course ||
          response?.data ||
          response?.product ||
          response?.course ||
          response;

        if (!courseData) {
          throw new Error("Course not found.");
        }

        setCourse(courseData);
      } catch (loadError) {
        console.error("Unable to load course:", loadError);

        if (isMounted) {
          setCourse(null);
          setError(
            "We couldn't load this course right now. Please return to the courses page and try again."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    if (courseSlug) {
      loadCourse();
    } else {
      setIsLoading(false);
      setError("Course not found.");
    }

    return () => {
      isMounted = false;
    };
  }, [courseSlug]);

  const courseTitle =
    course?.post_title ||
    course?.title ||
    course?.name ||
    "Course Details";

  /*
   * IMPORTANT:
   *
   * Do NOT use stripHtml() here.
   *
   * The API's post_content contains formatted HTML such as:
   *
   * <p>
   * <h2>
   * <h3>
   * <ul>
   * <li>
   * <strong>
   *
   * stripHtml() would remove those tags and produce the
   * single continuous paragraph seen in the screenshot.
   */
  const courseHtml = useMemo(() => {
    const html =
      course?.post_content ||
      course?.content ||
      course?.description ||
      "";

    return sanitizeCourseHtml(html);
  }, [course]);

  if (isLoading) {
    return (
      <main className="course-detail-page">
        <div className="course-detail-container">
          <div className="course-detail-status">
            <h1>Loading course...</h1>
            <p>Please wait while we retrieve the course information.</p>
          </div>
        </div>
      </main>
    );
  }

  if (error || !course) {
    return (
      <main className="course-detail-page">
        <div className="course-detail-container">
          <div className="course-detail-status course-detail-error">
            <h1>Course unavailable</h1>

            <p>{error || "The requested course could not be found."}</p>

            <Link
              to="/courses"
              className="course-detail-back-button"
            >
              Browse Courses
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="course-detail-page">
      <article className="course-detail-container">

        <nav
          className="course-detail-breadcrumb"
          aria-label="Breadcrumb"
        >
          <Link to="/courses">Courses</Link>

          <span aria-hidden="true">/</span>

          <span>{courseTitle}</span>
        </nav>

        <header className="course-detail-header">
          <h1>{courseTitle}</h1>
        </header>

        {courseHtml ? (
          <div
            className="course-detail-content"
            dangerouslySetInnerHTML={{
              __html: courseHtml,
            }}
          />
        ) : (
          <div className="course-detail-content">
            <p>
              Detailed course information is currently unavailable.
            </p>
          </div>
        )}

      </article>
    </main>
  );
}

export default CourseDetail;